import { Paper, Typography, TextField, Box, useTheme, DialogActions } from '@mui/material';
import { Dispatch, FC, MouseEvent, SetStateAction, useMemo, useState } from 'react';
import HelperText from '@shared/components/others/HelperText/HelperText';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { useForm } from 'react-hook-form';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { useTranslation } from 'react-i18next';
import PcapFilesUsedListDialog from './PcapFilesUsedListDialog';
import FolderIcon from '@mui/icons-material/Folder';
import { IP_PATTERN } from '@shared/utils/utilsOld';
import isEqual from 'lodash/isEqual';
import { PcapFileWithCps } from '../types';

export type TrexConfig = {
    client_ip_range: {
        from: string;
        to: string;
    };
    server_ip_range: {
        from: string;
        to: string;
    };
    multiplicator: number;
    dual_port_mask: string;
    pcap_files: PcapFileWithCps[];
};

interface TrexConfigFormProps {
    trexConfig: TrexConfig;
    setTrexConfig: Dispatch<SetStateAction<TrexConfig>>;
}

const TrexConfigForm: FC<TrexConfigFormProps> = ({ trexConfig, setTrexConfig }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [openPcapFilesUsedListDialog, setOpenPcapFilesUsedListDialog] = useState(false);

    const handleOpenPcapFilesUsedListDialog = () => {
        setOpenPcapFilesUsedListDialog(true);
    };

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        resetField,
        setValue,
        getValues,
    } = useForm({ defaultValues: trexConfig });

    const onSubmit = (data: TrexConfig) => {
        setTrexConfig(data);
        reset(data);
    };

    const pcapFiles = getValues('pcap_files');

    const handleCancelEditForm = (_e: MouseEvent<HTMLButtonElement>) => {
        reset();
    };

    const headerColor = useMemo(() => (theme.palette.mode === 'dark' ? '#5D9BD0' : '#2A4E69'), [theme.palette.mode]);

    return (
        <>
            <Box
                component={'form'}
                onSubmit={handleSubmit(onSubmit)}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}
            >
                <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: headerColor,
                        }}
                    >
                        1. {t('ip_addressing')}
                    </Typography>
                    <Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>{t('client_ip_range')}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'start' }}>
                            <TextField
                                {...register('client_ip_range.from', {
                                    required: t('field_required'),
                                    pattern: {
                                        value: IP_PATTERN,
                                        message: t('ip_address_helper_text'),
                                    },
                                })}
                                size="small"
                                error={Boolean(errors.client_ip_range?.from)}
                                placeholder={t('enter_valid_ip')}
                                helperText={errors.client_ip_range?.from?.message}
                            />
                            <ArrowRightAltIcon sx={{ mt: 1 }} />
                            <TextField
                                {...register('client_ip_range.to', {
                                    required: t('field_required'),
                                    pattern: {
                                        value: IP_PATTERN,
                                        message: t('ip_address_helper_text'),
                                    },
                                })}
                                size="small"
                                error={Boolean(errors.client_ip_range?.to)}
                                helperText={errors.client_ip_range?.to?.message}
                            />
                        </Box>
                        {!errors.client_ip_range?.from && !errors.client_ip_range?.to && (
                            <HelperText>{t('client_ip_range_helper')}</HelperText>
                        )}
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>{t('server_ip_range')}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'start' }}>
                            <TextField
                                {...register('server_ip_range.from', {
                                    required: t('field_required'),
                                    pattern: {
                                        value: IP_PATTERN,
                                        message: t('ip_address_helper_text'),
                                    },
                                })}
                                size="small"
                                error={Boolean(errors.server_ip_range?.from)}
                                helperText={errors.server_ip_range?.from?.message}
                            />
                            <ArrowRightAltIcon sx={{ mt: 1 }} />
                            <TextField
                                {...register('server_ip_range.to', {
                                    required: t('field_required'),
                                    pattern: {
                                        value: IP_PATTERN,
                                        message: t('ip_address_helper_text'),
                                    },
                                })}
                                size="small"
                                error={Boolean(errors.server_ip_range?.to)}
                                helperText={errors.server_ip_range?.to?.message}
                            />
                        </Box>
                        <HelperText>{t('server_ip_range_helper')}</HelperText>
                    </Box>
                </Paper>
                <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: headerColor,
                        }}
                    >
                        2. {t('scale_and_generation')}
                    </Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{t('multiplicator')}</Typography>
                    <TextField
                        size="small"
                        {...register('multiplicator', {
                            required: t('field_required'),
                        })}
                        error={Boolean(errors.multiplicator)}
                        helperText={errors.multiplicator?.message}
                        type="number"
                        slotProps={{
                            htmlInput: {
                                min: 1,
                            },
                        }}
                    />

                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{t('dual_port_mask')}</Typography>
                    <TextField
                        {...register('dual_port_mask', {
                            required: t('field_required'),
                            pattern: {
                                value: IP_PATTERN,
                                message: t('ip_address_helper_text'),
                            },
                        })}
                        size="small"
                        error={Boolean(errors.dual_port_mask)}
                        helperText={errors.dual_port_mask?.message}
                    />
                </Paper>
                <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: headerColor,
                        }}
                    >
                        3. {t('pcap_files_and_traffic')}
                    </Typography>
                    <Box>
                        <UtmsButton onClick={handleOpenPcapFilesUsedListDialog} startIcon={<FolderIcon />}>
                            {`${t('open_list_attachments')} (${pcapFiles.length})`}
                        </UtmsButton>
                    </Box>
                </Paper>
                <DialogActions sx={{ p: 0 }}>
                    <UtmsButton disabled={!isDirty && isEqual(trexConfig.pcap_files, pcapFiles)} type="submit">
                        {t('apply')}
                    </UtmsButton>
                    <UtmsButton onClick={handleCancelEditForm} color="secondary">
                        {t('revert_changes')}
                    </UtmsButton>
                </DialogActions>
            </Box>
            <PcapFilesUsedListDialog
                open={openPcapFilesUsedListDialog}
                setOpen={setOpenPcapFilesUsedListDialog}
                resetField={resetField}
                setValue={setValue}
                pcapFiles={pcapFiles}
            />
        </>
    );
};

export default TrexConfigForm;
